import { itemRootNode, itemSymbolNode } from "@corca/data/libraries/item";

import { normalizeSuggestionKeywords } from "../utils/normalizeKeywords";
import { getAutocompleteProps } from "../utils/operatorAutocomplete";

import type { SingleSuggestion } from "@/components/suggestions/types";
import type { Keyword, LibraryItem } from "@corca/data";

/**
 * The node types the editor matches by glyph; a single-root item of one of them gets that glyph
 * as its `symbol` so it ranks as the typed suggestion did. Eventually `symbol` goes and
 * `keywords` carry the glyph.
 */
const GLYPH_NODE_TYPES = new Set(["function", "variable", "constant", "operatorSymbol", "symbol"]);

function glyph(item: LibraryItem): string {
  const root = itemRootNode(item);
  const value = root && GLYPH_NODE_TYPES.has(root.type) ? itemSymbolNode(item)?.props.value : "";
  return typeof value === "string" ? value : "";
}

function rawKeyword(keyword: Keyword): string {
  return typeof keyword === "string" ? keyword : `${keyword.text} ^${keyword.weight}`;
}

function canBeAutocompleted(item: LibraryItem): SingleSuggestion<"library">["canBeAutocompleted"] {
  const { suggestion = {} } = item;

  if (suggestion.canBeAutocompleted !== undefined) {
    return suggestion.canBeAutocompleted;
  }

  if (itemRootNode(item)?.type === "operatorSymbol") {
    return getAutocompleteProps(glyph(item));
  }

  return undefined;
}

/**
 * A library item as the suggestion list shows and inserts it, with `type: "library"` so the
 * editor reads its nodes rather than props: the nodes are pasted in as they are and the cursor
 * goes to the item's target.
 */
export function libraryItemToSuggestion(item: LibraryItem): SingleSuggestion<"library"> {
  const { suggestion = {} } = item;

  return {
    type: "single",
    item: {
      type: "library",
      key: item.key,
      symbol: glyph(item),
      code: item.code,
      displayName: item.name,
      keywords: normalizeSuggestionKeywords({ keywords: item.keywords.map(rawKeyword) }),
      nodesJson: item.nodesJson,
      targetNode: suggestion.targetNode,
      fallbackTargetNode: suggestion.fallbackTargetNode,
      category: suggestion.category ?? "",
      labelPlaceholderVisible: suggestion.labelPlaceholderVisible,
      isSymbol: suggestion.isSymbol,
    },
    nestedKey: suggestion.nestedKey,
    sidebarGroupKey: suggestion.sidebarGroupKey,
    popupGroup: suggestion.popupGroup,
    context: suggestion.context,
    priority: suggestion.priority ?? 1,
    priorityRules: suggestion.priorityRules,
    inputMode: suggestion.inputMode,
    hideStandalone: suggestion.hideStandalone,
    canAutocomplete: suggestion.canAutocomplete,
    canBeAutocompleted: canBeAutocompleted(item),
  };
}

const suggestionByItem = new WeakMap<LibraryItem, SingleSuggestion<"library">>();

/**
 * The suggestion for an item, built once per item object.
 */
export function toSuggestion(item: LibraryItem): SingleSuggestion<"library"> {
  const cached = suggestionByItem.get(item);
  if (cached) return cached;

  const suggestion = libraryItemToSuggestion(item);
  suggestionByItem.set(item, suggestion);

  return suggestion;
}
